import React from 'react'
import { customerContainer } from 'src/models'
import { IS_BROWSER } from 'src/utils'
import { currencyFormat } from 'src/utils'
import './RelatedProductPrice.scss'

const getAccountType = () => {
  if (IS_BROWSER) {
    return customerContainer.profileResponse?.accounts?.type || ''
  }
  return ''
}

const isBrandAffiliateAccount = () => {
  const accountType = getAccountType()
  return (
    accountType === 'Brand Affiliate' ||
    accountType === 'Brand Affiliate - Business Entity'
  )
}

const isPreferredCustomer = () => {
  const accountType = getAccountType()
  return accountType === 'Preferred Customer/Member'
}

const RelatedProductPrice = ({ product, productType, className = '' }) => {
  const getProductPriceData = () => {
    const isBundleOrKit = productType === 'bundle' || productType === 'kit'

    if (isBundleOrKit) {
      return product?.priceFacets || product?.prices?.[0] || {}
    }

    return (
      product?.sku?.[0]?.totalValue ||
      product?.sku?.[0]?.prices?.[0] ||
      product?.sku?.[0]?.priceFacets?.[0] ||
      {}
    )
  }

  const extractPriceInformation = () => {
    const priceData = getProductPriceData()
    const priceFacets = priceData
    return {
      priceData,
      priceFacets,
      originalPrice: priceData.originalPrice,
      priceAfterDiscount: priceData.priceAfterDiscount,
      price: priceData.price || '',
      regularPrice: priceData['Regular Price'] || '',
      wholesalePrice: priceData['Wholesale Price'] || '',
    }
  }

  const getBrandAffiliatePricing = priceFacets => {
    const wholesalePriceFromFacets =
      priceFacets?.['Wholesale Price']?.['Wholesale Price'] ||
      priceFacets?.['Wholesale Price']

    const subWholesalePrice =
      priceFacets?.['Sub Wholesale Price']?.['Sub Wholesale Price'] ||
      priceFacets?.['Sub Wholesale Price']

    const subSBPrice =
      priceFacets?.['Sub SB']?.['Sub SB'] || priceFacets?.['Sub SB'] || 0

    const finalSubscriptionPrice =
      parseFloat(subWholesalePrice || 0) + parseFloat(subSBPrice || 0)

    return {
      wholesalePriceFromFacets,
      subWholesalePrice,
      subSBPrice,
      finalSubscriptionPrice,
    }
  }

  const renderBrandAffiliatePrice = priceFacets => {
    const {
      wholesalePriceFromFacets,
      subWholesalePrice,
      finalSubscriptionPrice,
    } = getBrandAffiliatePricing(priceFacets)

    if (subWholesalePrice && finalSubscriptionPrice > 0) {
      return (
        <span
          className="subscription-price"
          data-testid="qa-related-product-subscription-price">
          {currencyFormat({ price: wholesalePriceFromFacets })}
        </span>
      )
    }

    if (wholesalePriceFromFacets) {
      return (
        <span
          className="wholesale-price"
          data-testid="qa-related-product-wholesale-price">
          {currencyFormat({ price: wholesalePriceFromFacets })}
        </span>
      )
    }

    return null
  }

  const renderDiscountedPrice = (priceAfterDiscount, originalPrice) => {
    const priceToShow =
      priceAfterDiscount > 0 ? priceAfterDiscount : originalPrice || 0.0

    return (
      <span
        className="primary-price"
        data-testid="qa-related-product-sale-price">
        {currencyFormat({ price: priceToShow })}
      </span>
    )
  }

  const renderStandardPrice = (
    isBrandAffiliate,
    wholesalePrice,
    regularPrice,
    price
  ) => {
    const priceToShow = regularPrice || price || 0.0
    const priceClassName = isBrandAffiliate
      ? 'wholesale-price'
      : 'regular-price'
    const testId = isBrandAffiliate
      ? 'qa-related-product-wholesale-price'
      : 'qa-related-product-regular-price'

    return (
      <span className={priceClassName} data-testid={testId}>
        {currencyFormat({ price: priceToShow })}
      </span>
    )
  }

  const determineFinalPrice = () => {
    const {
      priceFacets,
      originalPrice,
      priceAfterDiscount,
      price,
      regularPrice,
      wholesalePrice,
    } = extractPriceInformation()

    const isBrandAffiliate = isBrandAffiliateAccount()
    if (isBrandAffiliate && priceFacets) {
      const baPrice = renderBrandAffiliatePrice(priceFacets)
      if (baPrice) return baPrice
    }

    const hasValidDiscount =
      priceAfterDiscount &&
      priceAfterDiscount !== originalPrice &&
      priceAfterDiscount > 0 &&
      originalPrice > 0

    if (hasValidDiscount) {
      return renderDiscountedPrice(priceAfterDiscount, originalPrice)
    }

    return renderStandardPrice(
      isBrandAffiliate,
      wholesalePrice,
      regularPrice,
      price
    )
  }

  return (
    <div className={`related-product-price ${className}`.trim()}>
      {determineFinalPrice()}
    </div>
  )
}

export {
  getAccountType,
  isBrandAffiliateAccount,
  isPreferredCustomer,
  RelatedProductPrice,
}

export default RelatedProductPrice
